import React from 'react'
import { List, AggregateListItem } from '../../../molecules/List/List'
import CheckIcon from '../../../atoms/Icons/Controls/Check'
import { LinearProgress } from '../../..'
import styles from './SidebarNav.module.scss'

interface SidebarNavItem {
  label?: string
  hasCheckmark?: boolean
  isOptional?: boolean
  isAlwaysAccessible?: boolean
  route: string
  isActive?: (url: string) => boolean
  progress?: number
  Icon?: React.ComponentType
}

interface SidebarNavProps {
  onClick?: (route: string) => void
  url: string
  items: SidebarNavItem[]
  isDraftPortfolio: boolean
}

const SidebarNav = ({ onClick, url, items, isDraftPortfolio }: SidebarNavProps) => {
  // How far the wizard has been worked through. Everything up to the step directly after the
  // furthest completed step is reachable, so a step whose previous steps are all done can be
  // opened from the nav instead of only via the previous step's "Next" button.
  const lastCompletedIndex = items.reduce((last, item, i) => (item.progress === 100 ? i : last), -1)

  return (
    <div className={styles.root}>
      <List interactive>
        {items.map((item, i) => {
          const isActive = item.isActive?.(url) ?? false
          const isCompleted = item.progress === 100

          // A completed step's category icon is replaced by a green check as the completion cue.
          // This is only meaningful while working through the draft wizard — on an activated
          // (non-draft) lease there is no outstanding work to signal, so every step keeps its
          // own icon.
          const showCompletedCheck = isDraftPortfolio && item.hasCheckmark && isCompleted
          const leftIcon = showCompletedCheck ? (
            <CheckIcon className={styles['check-complete']} />
          ) : (
            item.Icon && <item.Icon />
          )

          const itemText = (
            <>
              {leftIcon} {item.label}
              {isDraftPortfolio && item.isOptional && !item.hasCheckmark && (
                <span className={styles.optional}>&bull; optional</span>
              )}
            </>
          )
          // Sequential unlocking only applies inside the draft wizard; for an existing
          // (non-draft) portfolio every step stays freely navigable. Steps flagged
          // always-accessible (e.g. Internal) and the one you're currently on are never
          // muted; everything ahead of the wizard's furthest point stays locked and must be
          // reached via the "Next" button rather than the nav.
          const isStarted = (item.progress ?? 0) > 0
          const isReachable = isCompleted || isStarted || i <= lastCompletedIndex + 1
          const isLocked = isDraftPortfolio && !isActive && !isReachable && !item.isAlwaysAccessible

          return (
            <React.Fragment key={i}>
              <AggregateListItem
                key={i}
                text={itemText}
                active={isActive}
                disabled={isLocked}
                onClick={() => onClick?.(item.route)}
              />
              {!isDraftPortfolio && (
                <LinearProgress
                  percent={100}
                  strokeColor="rgba(0,0,0,0.10)"
                  trailColor="rgba(0,0,0,0.10)"
                  strokeWidth={0.3}
                />
              )}
              {isDraftPortfolio && item.progress !== undefined && (
                <LinearProgress
                  percent={item.progress}
                  strokeColor="rgba(0,0,0,0.10)"
                  trailColor="rgba(0,0,0,0.10)"
                  strokeWidth={0.3}
                />
              )}
            </React.Fragment>
          )
        })}
      </List>
    </div>
  )
}

export default SidebarNav
